import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ComplianceFinding } from '../models/quality.models';
import { ComplianceBadgeComponent } from './compliance-badge.component';
import { DateRangeFilterComponent } from './date-range-filter.component';
import { LoadingStateComponent } from './loading-state.component';
import { EmptyStateComponent } from './empty-state.component';
import { ErrorStateComponent } from './error-state.component';
import { ReviewStatusControlComponent } from './review-status-control.component';

@Component({
  selector: 'app-compliance-findings-widget',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    ComplianceBadgeComponent,
    DateRangeFilterComponent,
    LoadingStateComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    ReviewStatusControlComponent,
  ],
  templateUrl: './compliance-findings-widget.component.html',
  styleUrls: ['./compliance-findings-widget.component.scss'],
})
export class ComplianceFindingsWidgetComponent {
  @Input() title = '';
  @Input() voice = false;
  @Input() findings: ComplianceFinding[] = [];
  @Input() loading = false;
  @Input() error = '';
  @Input() updatingReviewKeys: ReadonlySet<string> = new Set();
  @Output() apply = new EventEmitter<{
    startDate: string;
    endDate: string;
    sk: string;
    limit: number;
  }>();
  @Output() clear = new EventEmitter<void>();
  @Output() refresh = new EventEmitter<void>();
  @Output() reviewStatusChange = new EventEmitter<ComplianceFinding>();

  expandedFinding: ComplianceFinding | null = null;
  showResults = true;

  applyFilters(
    startDate: string,
    endDate: string,
    sk: string,
    limit: number
  ): void {
    this.apply.emit({ startDate, endDate, sk, limit });
  }

  toggleReviewStatus(item: ComplianceFinding): void {
    this.reviewStatusChange.emit(item);
  }

  isUpdatingReview(item: ComplianceFinding): boolean {
    return this.updatingReviewKeys.has(`${item.pk}#${item.sk}`);
  }

  toggleShowResults(): void {
    this.showResults = !this.showResults;
  }

  hideCompleted = false;

  toggleHideCompleted(): void {
    this.hideCompleted = !this.hideCompleted;
  }

  get visibleFindings(): ComplianceFinding[] {
    return this.hideCompleted
      ? this.findings.filter((item) => item.reviewStatus !== 'review-complete')
      : this.findings;
  }

  get completedCount(): number {
    return this.findings.filter(
      (item) => item.reviewStatus === 'review-complete'
    ).length;
  }

  toggleExpand(item: ComplianceFinding): void {
    this.expandedFinding = this.expandedFinding === item ? null : item;
  }

  isExpanded(item: ComplianceFinding): boolean {
    return this.expandedFinding === item;
  }

  count(kind: 'pii' | 'riskyAdvice' | 'smoke'): number {
    return this.findings.filter((item) =>
      kind === 'smoke' ? item.smokeFireMentionCount > 0 : item[kind]
    ).length;
  }

  badge(
    item: ComplianceFinding,
    kind: 'pii' | 'risky' | 'smoke' | 'guardrail'
  ): { label: string; tone: 'red' | 'amber' | 'clear' } {
    if (kind === 'pii' && item.pii)
      return { label: 'PII detected', tone: 'red' };
    if (kind === 'risky' && item.riskyAdvice)
      return { label: 'Risky advice', tone: 'red' };
    if (kind === 'smoke' && item.smokeFireMentionCount > 0)
      return {
        label: `Smoke/fire: ${item.smokeFireMentionCount}`,
        tone: 'amber',
      };
    if (kind === 'guardrail' && item.guardrailsMessage)
      return { label: 'Guardrail message', tone: 'amber' };
    return { label: 'Clear', tone: 'clear' };
  }

  messageHistory(finding: ComplianceFinding): unknown[] {
    const history = finding.messageHistory;
    if (Array.isArray(history)) return history;
    if (typeof history === 'string') {
      try {
        const parsed: unknown = JSON.parse(history);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  messageRole(message: unknown): string {
    return this.messageField(message, 'role') || 'Conversation message';
  }

  messageText(message: unknown): string {
    const content =
      this.messageValue(message, 'content') ??
      this.messageValue(message, 'message');
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      return content
        .map((block) => this.contentText(block))
        .filter(Boolean)
        .join('\n');
    }
    return this.contentText(content);
  }

  messageTimestamp(message: unknown): string {
    return this.messageField(message, 'timestamp');
  }

  private contentText(content: unknown): string {
    if (typeof content === 'string') return content;
    if (!content || typeof content !== 'object') return '';
    const record = content as Record<string, unknown>;
    return typeof record['text'] === 'string'
      ? record['text']
      : typeof record['message'] === 'string'
        ? record['message']
        : '';
  }

  private messageField(message: unknown, field: string): string {
    const value = this.messageValue(message, field);
    return typeof value === 'string' ? value : '';
  }

  private messageValue(message: unknown, field: string): unknown {
    return message && typeof message === 'object'
      ? (message as Record<string, unknown>)[field]
      : undefined;
  }
}
